import React from "react";
import PolicyLayout from "../../components/PolicyLayout";
import s from "../../styles/SupportPage.module.css";

const APPS = [
  "White Noise: Calm Sleep",
  "QR & Barcode Scanner: Fast Reader Pro",
  "Smart GPS Compass",
  "Note Spark: AI Note Taker",
  "Magic Eraser: Remove Object",
  "Seek AI Chat",
  "Nola AI Chat Friend",
  "AI Homework Solver",
  "Veilify Photo Vault",
  "Voice Changer Pro",
  "AI Calorie Counter: Food Tracker",
  "Invoice Maker for Contractors",
  "Smart Spy Detector (MVP)",
  "Smart Collage AI",
  "TraderLab",
  "WishFlow",
  "BrainIQ: Boost Your Mind",
  "PureTracker: Quit Vaping",
  "PureTracker: Quit Drinking",
  "Pupverse: Dog Training",
  "Schedula",
];

const FAQ_ITEMS = [
  {
    id: "getting-started",
    question: "I installed an app. How do I get started?",
    answer: (
      <p>
        Open the app and look for the first screen or a Getting Started section.
        Most GoodWave Lab apps show a short onboarding with tips the first time
        you open them. If something looks broken (blank screen or crash), try
        restarting the app and making sure you are on the latest version from
        the App Store or Google Play.
      </p>
    ),
  },
  {
    id: "restore-purchases",
    question: "I bought Premium but the app still shows the paywall.",
    answer: (
      <p>
        Make sure you are using the same Apple ID or Google Play account that
        you used to purchase the subscription. Then open the app, go to the
        Settings or Premium screen and tap the button labeled Restore Purchases
        or Restore. If the purchase is still not restored, please send us a
        screenshot of your receipt from Apple or Google Play and we will help
        you check the entitlement state.
      </p>
    ),
  },
  {
    id: "cancel-subscription",
    question: "How do I cancel a subscription or free trial?",
    answer: (
      <p>
        All subscriptions are managed by Apple or Google. To cancel, open the
        App Store or Google Play on your device, go to your account, then
        Subscriptions, select the app and choose Cancel. Deleting the app does
        not automatically cancel your subscription. We cannot cancel or refund
        purchases directly, but we can help you check which product you are
        subscribed to.
      </p>
    ),
  },
  {
    id: "crash-or-bug",
    question: "The app crashes or something does not work as expected.",
    answer: (
      <p>
        First, restart the app and your device, and make sure you have the
        latest version installed from the store. If the problem remains, please
        contact us with details: which app, what you were trying to do, your
        device model, OS version, and if possible screenshots or screen
        recordings. This helps us reproduce and fix the issue faster.
      </p>
    ),
  },
  {
    id: "privacy-rights",
    question: "How can I delete my data or make a privacy request?",
    answer: (
      <p>
        Each app has a dedicated Privacy Policy that explains how data is
        processed and how you can request access, deletion, correction,
        restriction, or other rights where applicable. Usually you can email{" "}
        <a
          href="mailto:support@goodwavelab.com"
          className={s.link}
        >
          support@goodwavelab.com
        </a>{" "}
        from the same address you use inside the app. Please include the app
        name, sign in method (Apple, Google, or email, if used), and a short
        description of your request.
      </p>
    ),
  },
];

export default function SupportPage() {
  return (
    <PolicyLayout
      title="support"
      appName="GoodWave Lab Apps"
      date="DECEMBER 2025"
    >
      {/* Intro */}
      <section className={s.intro}>
        <h1 className={s.headline}>How can we help?</h1>
        <p className={s.subtitle}>
          This page is the central support hub for all GoodWave Lab applications
          on the App Store and Google Play. Choose a topic below, read the FAQ,
          or contact us directly if you need personal assistance.
        </p>
      </section>

      {/* FAQ */}
      <section className={s.section}>
        <h2 className={s.sectionTitle}>Frequently asked questions</h2>
        <div className={s.faqList}>
          {FAQ_ITEMS.map((item) => (
            <details key={item.id} className={s.faqItem}>
              <summary className={s.faqQuestion}>
                <span>{item.question}</span>
                <span className={s.faqIcon}>+</span>
              </summary>
              <div className={s.faqAnswer}>{item.answer}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Contact us */}
      <section className={s.section}>
        <h2 className={s.sectionTitle}>Contact us</h2>
        <p className={s.sectionIntro}>
          Did not find what you were looking for in the FAQ? Reach out and we
          will do our best to help. Please include as much detail as possible so
          we can understand the issue quickly.
        </p>

        <div className={s.contactGrid}>
          <div className={s.contactCard}>
            <h3 className={s.contactTitle}>Email support</h3>
            <p className={s.contactText}>
              The fastest way to reach us is by email. We usually reply within a
              few business days.
            </p>
            <p className={s.contactHighlight}>
              <strong>Email:</strong>{" "}
              <a
                href="mailto:support@goodwavelab.com"
                className={s.link}
              >
                support@goodwavelab.com
              </a>
              <br />
              <br />
              When you write, please include:
              <br />- App name
              <br />- Platform (iOS or Android)
              <br />- Device model and OS version
              <br />- Short description of the issue
            </p>
          </div>

          <div className={s.contactCard}>
            <h3 className={s.contactTitle}>Contact form</h3>
            <p className={s.contactText}>
              You can also use this simple form. It opens your default email app
              with the details filled in, so you can add screenshots if needed.
            </p>

            <form
              className={s.form}
              action="mailto:support@goodwavelab.com"
              method="post"
              encType="text/plain"
            >
              <div className={s.formRow}>
                <label htmlFor="appName">Which app?</label>
                <select id="appName" name="App">
                  <option value="">Select an app</option>
                  {APPS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  <option value="Other">Other / not listed</option>
                </select>
              </div>

              <div className={s.formRow}>
                <label htmlFor="email">Your email</label>
                <input
                  id="email"
                  name="Email"
                  type="email"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className={s.formRow}>
                <label htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  name="Subject"
                  type="text"
                  placeholder='Short summary (for example: "Cannot restore Premium")'
                  required
                />
              </div>

              <div className={s.formRow}>
                <label htmlFor="message">Describe the issue</label>
                <textarea
                  id="message"
                  name="Message"
                  rows={5}
                  placeholder="Device model, OS version, app version, what you tried, and what happened."
                  required
                />
              </div>

              <button type="submit" className={s.submitButton}>
                Open email and send
              </button>

              <p className={s.formNote}>
                If the button does not work on your device, please write to{" "}
                <a
                  href="mailto:support@goodwavelab.com"
                  className={s.link}
                >
                  support@goodwavelab.com
                </a>{" "}
                manually and include the same details.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Subscriptions and billing */}
      <section className={s.section}>
        <h2 className={s.sectionTitle}>Subscriptions and billing</h2>
        <p className={s.sectionIntro}>
          Many of our apps offer optional Premium subscriptions. These are
          billed and managed by Apple or Google; we do not process payments
          directly.
        </p>

        <div className={s.billingList}>
          <div className={s.billingItem}>
            <h3 className={s.billingTitle}>Managing your subscription</h3>
            <p className={s.billingText}>
              To view, change, or cancel a subscription, open the App Store or
              Google Play, go to your account, then Subscriptions, select the
              relevant app, and choose the option you need. You can also see the
              next billing date and price on that screen.
            </p>
          </div>

          <div className={s.billingItem}>
            <h3 className={s.billingTitle}>Restoring purchases</h3>
            <p className={s.billingText}>
              If you reinstall an app or switch to a new device, open the app,
              go to the Premium screen or Settings, and tap Restore Purchases
              (sometimes called Restore). Make sure you are signed in with the
              same Apple ID or Google account that you used to buy the
              subscription.
            </p>
          </div>

          <div className={s.billingItem}>
            <h3 className={s.billingTitle}>Refunds and billing issues</h3>
            <p className={s.billingText}>
              Refund decisions are made by Apple and Google according to their
              own policies. If you believe you were charged by mistake, open the
              store receipt and use the Report a Problem link, or search for
              "Apple report a problem" or "Google Play refund" in your browser.
              After the store processes your case, we can help verify that your
              app entitlement matches the final status.
            </p>
          </div>
        </div>
      </section>
    </PolicyLayout>
  );
}
